import { OnboardingValues } from 'modules/onboarding/steps/BasicInfo/BasicInfoStep';
import { BasicInfoTabValues } from 'modules/profile/types/BasicInfoTabValues';
import { EducationTabValues } from 'modules/profile/types/EducationTabValues';
import { ExperienceTabValues } from 'modules/profile/types/ExperienceTabValues';
import { LanguagesTabValues } from 'modules/profile/types/LanguagesTabValues';
import { ProfileTabEnum } from 'modules/profile/types/profile-tab.enum';
import { SkillLevelTabValues } from 'modules/profile/types/SkillLevelTabValues';
import { SkillTabValues } from 'modules/profile/types/SkillTabValues';
import { extractSkillsFromPrevStates } from 'modules/profile/utils/extractSkillsFromPrevStates';
import { filterEmptyCertificates } from 'modules/profile/utils/filterEmptyCertificates';
import { filterEmptyEducation } from 'modules/profile/utils/filterEmptyEducation';
import { ValuesByName } from 'modules/profile/utils/mapValuesForApiUpdate';
import { ExpertDataQuery } from 'shared/hooks/api/expertData/expertData.generated';
import { ExpertProfile } from 'types/ExpertProfile/ExpertProfile';
import { mapDtoToExpertProfile } from 'types/ExpertProfile/mappers';
import { Skill } from 'types/Skill';
import { WorkHistory } from 'types/WorkHistory';
import { create } from 'zustand';

interface ExpertProfileStore {
  expertProfile: ExpertProfile | null;
  updateExpertProfile: (params: Partial<ExpertProfile>) => void;
  loadExpertProfile: (
    dto: ExpertDataQuery['expertHubData'],
    exportId?: string,
    email?: string,
  ) => void;
  updateExpertProfileFromOnboarding: (onboardingValues: OnboardingValues) => void;
  updateExpertProfileFromProfile: <T extends ProfileTabEnum>(
    tab: T,
    newValues: Partial<ValuesByName[T]>,
  ) => void;
  clearExpertProfile: () => void;
}

export const useExpertProfileStore = create<ExpertProfileStore>((set) => ({
  expertProfile: null,
  loadExpertProfile: (dto: ExpertDataQuery['expertHubData'], exportId?: string, email?: string) =>
    set((prev) => ({
      expertProfile: {
        ...mapDtoToExpertProfile(dto),
        id: exportId ?? prev.expertProfile?.id ?? '',
        email: email ?? prev.expertProfile?.email ?? '',
      },
    })),
  updateExpertProfile: (params: Partial<ExpertProfile>) =>
    set((prev) => ({
      ...prev,
      ...(prev.expertProfile && {
        expertProfile: {
          ...prev.expertProfile,
          ...params,
        },
      }),
    })),
  updateExpertProfileFromOnboarding: (onboardingValues: OnboardingValues) =>
    set((prev) => ({
      ...(prev.expertProfile && {
        expertProfile: {
          ...prev.expertProfile,
          ...(onboardingValues.firstName && { firstName: onboardingValues.firstName }),
          ...(onboardingValues.lastName && { lastName: onboardingValues.lastName }),
        },
      }),
    })),
  updateExpertProfileFromProfile: (tab, newValues) => {
    set((prev) => {
      if (!prev.expertProfile) {
        return prev;
      }
      let updatedValues: Partial<ExpertProfile> = {};

      switch (tab) {
        case ProfileTabEnum.Basic: {
          const basicInfoValues = newValues as Partial<BasicInfoTabValues>;
          updatedValues = {
            ...(basicInfoValues.firstName && {
              firstName: basicInfoValues.firstName,
            }),
            ...(basicInfoValues.lastName && {
              lastName: basicInfoValues.lastName,
            }),
            ...(basicInfoValues.availability && {
              availability: basicInfoValues.availability,
            }),
            ...(basicInfoValues.location && {
              location: basicInfoValues.location,
            }),
            ...(basicInfoValues.rateDesired && {
              rateDesired: basicInfoValues.rateDesired,
            }),
            ...(basicInfoValues.workType && {
              workType: basicInfoValues.workType,
            }),
            ...(basicInfoValues.phoneNumber && {
              phoneNumber: basicInfoValues.phoneNumber,
            }),
            ...(typeof basicInfoValues.linkedInUrl !== 'undefined' && {
              linkedInUrl: basicInfoValues.linkedInUrl,
            }),
          };

          break;
        }
        case ProfileTabEnum.Languages: {
          const langValues = newValues as Partial<LanguagesTabValues>;
          updatedValues = {
            ...(langValues.firstLanguageId && {
              firstLanguageId: langValues.firstLanguageId,
            }),
            ...(langValues.isOnlyFirstLanguage !== undefined && {
              isOnlyFirstLanguage: langValues.isOnlyFirstLanguage,
            }),
            ...(langValues.isOnlyFirstLanguage
              ? []
              : langValues.otherLanguages && {
                  otherLanguages: langValues.otherLanguages,
                }),
          };

          break;
        }
        case ProfileTabEnum.Experience: {
          const experienceValues = newValues as Partial<ExperienceTabValues>;
          const skillsAlreadyInProfile = prev.expertProfile.skills ?? [];
          const extractedSkills = extractSkillsFromPrevStates(
            [
              ...((experienceValues.mainWorkHistory ?? []) as WorkHistory[]),
              ...((experienceValues.otherWorkHistory ?? []) as WorkHistory[]),
            ],
            skillsAlreadyInProfile,
          );

          const updatedMainWorkHistory = experienceValues.isMainRoleExperience
            ? (experienceValues.mainWorkHistory as WorkHistory[])
            : [];
          const updatedOtherWorkHistory = experienceValues.isOtherRolesExperience
            ? (experienceValues.otherWorkHistory as WorkHistory[])
            : [];

          updatedValues = {
            ...(experienceValues.mainWorkHistory && {
              mainWorkHistory: updatedMainWorkHistory,
              isMainRoleExperience: !!updatedMainWorkHistory.length,
            }),
            ...(experienceValues.otherWorkHistory && {
              otherWorkHistory: updatedOtherWorkHistory,
              isOtherRolesExperience: !!updatedOtherWorkHistory.length,
            }),
            ...(experienceValues.jobPositionId && {
              jobPositionId: experienceValues.jobPositionId,
            }),
            skills: extractedSkills,
          };

          break;
        }

        case ProfileTabEnum.Skill: {
          const skillValues = newValues as Partial<SkillTabValues & SkillLevelTabValues>;
          const manualSkillsAlreadyInProfile: Skill[] = skillValues.jobPositionSkills
            ? skillValues.jobPositionSkills.tagIds.map((tagId) => ({
                tagId,
                weight:
                  prev.expertProfile?.manuallyAddedSkills.find((tag) => tag.tagId === tagId)
                    ?.weight || 1,
              }))
            : (prev.expertProfile.manuallyAddedSkills ?? []);

          const extractedSkills = extractSkillsFromPrevStates(
            [...(skillValues.mainWorkHistory ?? []), ...(skillValues.otherWorkHistory ?? [])],
            [
              ...(prev.expertProfile.skills ?? []),
              ...(prev.expertProfile.manuallyAddedSkills ?? []),
            ],
          );

          updatedValues = {
            ...(skillValues.mainWorkHistory && {
              mainWorkHistory: skillValues.mainWorkHistory as WorkHistory[],
            }),
            ...(skillValues.otherWorkHistory && {
              otherWorkHistory: skillValues.otherWorkHistory as WorkHistory[],
            }),
            skills: extractedSkills,
            manuallyAddedSkills: manualSkillsAlreadyInProfile,
          };

          break;
        }

        case ProfileTabEnum.SkillLevel: {
          const skillLevelValues = newValues as Partial<SkillLevelTabValues>;
          updatedValues = {
            ...(skillLevelValues.skills && {
              skills: skillLevelValues.skills,
            }),
            ...(skillLevelValues.manuallyAddedSkills && {
              manuallyAddedSkills: skillLevelValues.manuallyAddedSkills,
            }),
          };

          break;
        }

        case ProfileTabEnum.Education: {
          const educationValues = newValues as Partial<EducationTabValues>;
          updatedValues = {
            ...(educationValues.certificates && {
              certificates: filterEmptyCertificates(educationValues.certificates),
            }),
            ...(educationValues.education && {
              education: filterEmptyEducation(educationValues.education),
            }),
          };

          break;
        }

        default:
          throw new Error(`Unhandled tab update: ${tab}`);
      }

      return {
        expertProfile: {
          ...prev.expertProfile,
          ...updatedValues,
        },
      };
    });
  },
  clearExpertProfile: () => set({ expertProfile: null }),
}));
